import React, { useState, useEffect } from 'react';
import './BarAvailabilityStatus.css';

const BarAvailabilityStatus = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      console.log('Fetching BAR availability...');
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        'https://mansion-facility-wtr-reservation-api.info1499.workers.dev/api/v1/facilities/bar/availability?days=10',
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          mode: 'cors'
        }
      );
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`API Error (${response.status}): ${errorText || 'Failed to fetch availability'}`);
      }

      const data = await response.json();
      console.log('Received data:', data);
      
      // Filter out past dates and ensure today is first
      if (data && data.days) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const filteredDays = data.days.filter(day => {
          const dayDate = new Date(day.date.replace(/\//g, '-'));
          dayDate.setHours(0, 0, 0, 0);
          return dayDate >= today;
        });
        
        data.days = filteredDays;
      }
      
      setAvailability(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr.replace(/\//g, '-'));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    const isToday = compareDate.getTime() === today.getTime();
    const isPast = compareDate < today;
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
    
    return {
      month: monthNames[date.getMonth()],
      day: date.getDate(),
      weekday: dayNames[date.getDay()],
      isToday,
      isPast,
      dateObj: compareDate
    };
  };

  const getAvailabilityStatus = (slots) => {
    if (!slots || slots.length === 0) return { available: false, icon: '❓', text: '情報なし' };
    
    // Check if there are any booked slots (貸し切り)
    const bookedSlots = slots.filter(slot => slot.status === 'booked');
    const pastDeadlineSlots = slots.filter(slot => slot.status === 'past-deadline');
    const availableSlots = slots.filter(slot => slot.status === 'available' || slot.available);
    
    // If all slots are past deadline, check if they would have been available
    if (pastDeadlineSlots.length === slots.length) {
      // For past-deadline dates, we need to check if there are actually slots that would be available
      // If no slots show as available (even past deadline), it means the facility is fully booked
      const hasAnyAvailableTime = slots.some(slot => slot.available === true);
      
      if (!hasAnyAvailableTime) {
        // All slots are booked for this day
        return { 
          available: false, 
          icon: '🚪', 
          text: '貸し切り',
          subtext: '終日予約済み'
        };
      } else {
        // Some slots would be available for walk-in
        return { 
          available: true, 
          icon: '🍸', 
          text: '利用可',
          subtext: null
        };
      }
    }
    
    // If there are any booked slots, it means there's a private reservation
    if (bookedSlots.length > 0) {
      // Check if entire evening is booked
      if (bookedSlots.length === slots.filter(s => s.status !== 'past-deadline').length) {
        return { 
          available: false, 
          icon: '🚪', 
          text: '貸し切り',
          subtext: '終日予約済み'
        };
      } else {
        // Partial booking - show which hours are free
        const freeSlots = slots.filter(slot => slot.available || slot.status === 'available');
        const freeHours = freeSlots.map(slot => slot.time).join(', ');
        return { 
          available: false, 
          icon: '⏰', 
          text: '一部貸し切り',
          subtext: `利用可能: ${freeHours}`
        };
      }
    }
    
    // No bookings = freely accessible
    return { 
      available: true, 
      icon: '🍸', 
      text: '自由利用可',
      subtext: null
    };
  };

  if (loading) {
    return (
      <div className="bar-availability-container">
        <h3 className="bar-availability-title">🍸 BAR 利用状況</h3>
        <div className="bar-availability-loading">読み込み中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bar-availability-container">
        <h3 className="bar-availability-title">🍸 BAR 利用状況</h3>
        <div className="bar-availability-error">エラー: {error}</div>
      </div>
    );
  }

  if (!availability || !availability.days) {
    return null;
  }

  const daysToShow = expanded ? 7 : 3;
  const displayDays = availability.days.slice(0, daysToShow);

  return (
    <div className="bar-availability-container">
      <h3 className="bar-availability-title">🍸 BAR 利用状況</h3>
      <div className="bar-availability-grid">
        {displayDays.map((day, index) => {
          const dateInfo = formatDate(day.date);
          const status = getAvailabilityStatus(day.slots);
          
          return (
            <div key={index} className={`bar-availability-day ${dateInfo.isToday ? 'is-today' : ''}`}>
              <div className="bar-availability-date">{dateInfo.month}{dateInfo.day}日</div>
              <div className={`bar-availability-dayname ${dateInfo.isToday ? 'is-today' : ''}`}>
                {dateInfo.weekday}曜日
                {dateInfo.isToday && ' (今日)'}
              </div>
              <div className="bar-availability-icon">{status.icon}</div>
              <div className={`bar-availability-status ${status.available ? 'available' : 'unavailable'}`}>
                {status.text}
              </div>
              {status.subtext && (
                <div className="bar-availability-timeslots">{status.subtext}</div>
              )}
            </div>
          );
        })}
      </div>
      
      {availability.days.length > 3 && (
        <button 
          className="bar-availability-expand-button"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '表示を減らす ▲' : 'さらに表示 ▼'}
        </button>
      )}
      
      <div className="bar-availability-legend">
        <div className="bar-availability-legend-item">
          <span>🍸</span>
          <span>利用可</span>
        </div>
        <div className="bar-availability-legend-item">
          <span>⏰</span>
          <span>一部貸し切り</span>
        </div>
        <div className="bar-availability-legend-item">
          <span>🚪</span>
          <span>貸し切り（利用不可）</span>
        </div>
      </div>
    </div>
  );
};

export default BarAvailabilityStatus;